import { logger } from '../../utils/logger';
import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DownloadIcon, FileSpreadsheetIcon, BriefcaseIcon, LockIcon, TrendingUpIcon, DollarSignIcon, ActivityIcon, UserIcon, BarChart3Icon, XIcon, CalendarIcon, FilterIcon, ListIcon, LayoutGridIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, PieChartIcon, ClockIcon, FileTextIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Service } from '../../types';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
import { exportServicesToExcel } from '../../utils/excelExport';
import { exportServicesToPdf, exportServiceDetailToPdf, exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { hasPermission } from '../../types';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
const COMPARE_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
export function Services({
  autoOpenNew = false
}: {
  autoOpenNew?: boolean;
} = {}) {
  const {
    services,
    addService,
    updateService,
    deleteService,
    doctors,
    appointments,
    getDoctor,
    getSpecialty
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('services');
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canViewStats = hasPermission(user?.role || '', 'services_stats', 'read');
  const canDownloadStatsPdf = isAdmin || user?.role === 'supervisor';
  // Tab State
  const [activeTab, setActiveTab] = useState('gestion');
  // Management State
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-services-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  // Load deactivated/archived statuses for doctors (to filter from assignment)
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-doctors-status');
      if (saved) setDoctorStatuses(JSON.parse(saved));
    } catch {}
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const activeDoctors = doctors.filter(d => isDoctorActive(d.id));
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateService(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateService(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateService(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  // Analytics State
  const [compareServiceIds, setCompareServiceIds] = useState<string[]>([]);
  const [compareSearchTerm, setCompareSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '2020-01-01',
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month'); // 'day', 'week', 'month'
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    duration: 30,
    price: '',
    cost: '',
    description: '',
    doctorIds: [] as string[]
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filtered services for Management
  const filteredServices = services.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(s.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedServices,
    currentPage: servicesPage,
    totalPages: servicesTotalPages,
    totalItems: servicesTotalItems,
    startIndex: servicesStartIndex,
    endIndex: servicesEndIndex,
    goToPage: servicesGoToPage,
    nextPage: servicesNextPage,
    prevPage: servicesPrevPage
  } = usePagination(filteredServices);
  // Counts for tabs
  const activeCount = services.filter(s => getItemStatus(s.id) === 'active').length;
  const archivedCount = services.filter(s => getItemStatus(s.id) === 'archived').length;
  const deactivatedCount = services.filter(s => getItemStatus(s.id) === 'deactivated').length;
  // Filtered services for Analytics Selector
  const filteredCompareServices = services.filter(s => s.name.toLowerCase().includes(compareSearchTerm.toLowerCase()));
  // --- ANALYTICS CALCULATIONS ---
  // 1. Filter appointments based on date range and status
  const filteredAppointments = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999); // End of the day
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return apt.status === 'completada' && aptDate >= start && aptDate <= end;
    });
  }, [appointments, dateRange]);
  // 2. Aggregate data based on Group By and Selected Services
  const chartData = useMemo(() => {
    const dataMap: Record<string, any> = {};
    // Helper to generate keys based on group by
    const getKey = (date: Date) => {
      if (groupBy === 'day') return date.toISOString().split('T')[0];
      if (groupBy === 'week') {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
        return `${d.getFullYear()}-W${weekNo}`;
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // month
    };
    // Initialize map with 0s for selected services if needed, or just build dynamically
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      // If comparing specific services, check if any of the appointment's services match
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      const date = new Date(apt.date);
      const key = getKey(date);
      if (!dataMap[key]) {
        dataMap[key] = {
          name: key,
          totalCount: 0,
          totalRevenue: 0
        };
      }
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          dataMap[key][service.name] = (dataMap[key][service.name] || 0) + 1;
          dataMap[key][`${service.name}_revenue`] = (dataMap[key][`${service.name}_revenue`] || 0) + service.price;
          dataMap[key].totalCount += 1;
          dataMap[key].totalRevenue += service.price;
        }
      });
    });
    // Sort by date key
    return Object.values(dataMap).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [filteredAppointments, groupBy, compareServiceIds, services]);
  // 3. Summary Table Data
  const summaryData = useMemo(() => {
    const summary: Record<string, {
      count: number;
      revenue: number;
      cost: number;
      name: string;
    }> = {};
    // Initialize for selected services to ensure they appear even if 0
    if (compareServiceIds.length > 0) {
      compareServiceIds.forEach(id => {
        const s = services.find(ser => ser.id === id);
        if (s) summary[id] = {
          count: 0,
          revenue: 0,
          cost: s.cost || 0,
          name: s.name
        };
      });
    } else {
      // If none selected, initialize all
      services.forEach(s => {
        summary[s.id] = {
          count: 0,
          revenue: 0,
          cost: s.cost || 0,
          name: s.name
        };
      });
    }
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          if (!summary[service.id]) summary[service.id] = {
            count: 0,
            revenue: 0,
            cost: (service as any).cost || 0,
            name: service.name
          };
          summary[service.id].count += 1;
          summary[service.id].revenue += service.price;
        }
      });
    });
    return Object.values(summary).sort((a, b) => b.revenue - a.revenue);
  }, [filteredAppointments, compareServiceIds, services]);
  // 4. KPI Calculations
  const kpiData = useMemo(() => {
    const totalRevenue = summaryData.reduce((sum, item) => sum + item.revenue, 0);
    const totalCount = summaryData.reduce((sum, item) => sum + item.count, 0);
    const topService = summaryData.length > 0 ? summaryData[0] : null;
    const avgTicket = totalCount > 0 ? totalRevenue / totalCount : 0;
    return {
      totalRevenue,
      totalCount,
      topService,
      avgTicket
    };
  }, [summaryData]);
  // Pagination for summary table in statistics tab
  const {
    paginatedItems: paginatedSummary,
    currentPage: summaryPage,
    totalPages: summaryTotalPages,
    totalItems: summaryTotalItems,
    startIndex: summaryStartIndex,
    endIndex: summaryEndIndex,
    goToPage: summaryGoToPage,
    nextPage: summaryNextPage,
    prevPage: summaryPrevPage
  } = usePagination(summaryData, {
    itemsPerPage: 10
  });
  // --- HANDLERS ---
  const openNewForm = () => {
    if (!canCreate('services')) return;
    setSelectedService(null);
    setFormData({
      name: '',
      sku: '',
      duration: 30,
      price: '',
      cost: '',
      description: '',
      doctorIds: []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Auto-open new form when navigated with :new param
  useEffect(() => {
    if (autoOpenNew) {
      const t = setTimeout(() => openNewForm(), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const openEditForm = (service: Service) => {
    if (!canEdit('services')) return;
    setSelectedService(service);
    setFormData({
      name: service.name,
      sku: service.sku || '',
      duration: service.duration,
      price: String(service.price),
      cost: service.cost ? String(service.cost) : '',
      description: service.description,
      doctorIds: service.doctorIds || []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (service: Service, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedService(service);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedService || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedService.id);
      showToast({
        type: 'info',
        title: 'Servicio archivado',
        message: `${selectedService.name} fue archivado.`
      });
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedService.id);
      showToast({
        type: 'warning',
        title: 'Servicio desactivado',
        message: `${selectedService.name} fue desactivado.`
      });
    } else if (actionType === 'delete') {
      deleteService(selectedService.id);
      showToast({
        type: 'error',
        title: 'Servicio eliminado',
        message: `${selectedService.name} fue eliminado permanentemente.`
      });
      setIsActionConfirmOpen(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.duration <= 0) errors.duration = 'La duración debe ser mayor a 0';
    const priceNum = parseInt(formData.price, 10);
    if (isNaN(priceNum) || priceNum < 0) errors.price = 'El precio no puede ser negativo';
    const costNum = formData.cost === '' ? 0 : parseInt(formData.cost, 10);
    if (isNaN(costNum) || costNum < 0) errors.cost = 'El costo no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const dataToSave = {
      name: formData.name,
      sku: formData.sku || undefined,
      duration: formData.duration,
      price: priceNum,
      cost: costNum,
      description: formData.description,
      doctorIds: formData.doctorIds
    };
    try {
      // ESTO ES LO QUE HACE QUE SE GUARDE:
      if (selectedService) {
        await updateService(selectedService.id, dataToSave);
        showToast({
          type: 'success',
          title: 'Servicio actualizado',
          message: `${formData.name} fue actualizado correctamente.`
        });
      } else {
        await addService(dataToSave);
        showToast({
          type: 'success',
          title: 'Servicio creado',
          message: `${formData.name} fue registrado correctamente.`
        });
      }
      // Solo se cierra el cuadro si la base de datos respondió "OK"
      setIsFormOpen(false);
    } catch (error) {
      logger.error('Error al guardar:', error);
      showToast({
        type: 'error',
        title: 'Error al guardar',
        message: 'No se pudo guardar. Revisa tu internet o si el SKU ya existe.'
      });
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToExcel(services);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToPdf(services);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const toggleDoctorSelection = (doctorId: string) => {
    setFormData(prev => {
      const currentIds = prev.doctorIds || [];
      return currentIds.includes(doctorId) ? {
        ...prev,
        doctorIds: currentIds.filter(id => id !== doctorId)
      } : {
        ...prev,
        doctorIds: [...currentIds, doctorId]
      };
    });
  };
  const toggleCompareService = (serviceId: string) => {
    setCompareServiceIds(prev => prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]);
  };
  const tabs = [{
    id: 'gestion',
    label: 'Gestión de Servicios',
    icon: <LayoutGridIcon className="w-4 h-4" data-id="element-4695" />
  }, ...(canViewStats ? [{
    id: 'estadisticas',
    label: 'Estadísticas y Reportes',
    icon: <BarChart3Icon className="w-4 h-4" data-id="element-4696" />
  }] : [])];
  return <div className="space-y-6" data-id="element-4697">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl p-6 text-white shadow-xl" style={{
      background: "linear-gradient(135deg,#0c4a6e,#0284c7,#38bdf8)"
    }} data-id="element-4698">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-4699" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4700" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4701" />
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.15) 100%)'
      }} data-id="element-4702" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-4703" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-4704">
          <div data-id="element-4705">
            <div className="flex items-center gap-3 mb-2" data-id="element-4706">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4707">
                <BriefcaseIcon className="w-6 h-6 text-white" data-id="element-4708" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-4709">Servicios</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-4710">Administración y análisis de servicios médicos</p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4711">
            {canExportPdf && <Button variant="outline" onClick={() => setIsPdfExportModalOpen(true)} isLoading={isPdfExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<BriefcaseIcon className="w-4 h-4" data-id="element-4713" />} data-id="element-4712">Exportar PDF</Button>}
            {canExportExcel && <Button variant="outline" onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4715" />} data-id="element-4714">Exportar Excel</Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-4716">
        {/* ==================== TAB 1: GESTIÓN ==================== */}
        <TabPanel id="gestion" activeTab={activeTab} data-id="element-4717">
          <div className="space-y-6" data-id="element-4718">
            {/* Actions Bar */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-4719">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto" data-id="element-4720">
                <div className="w-full sm:w-64 relative" data-id="element-4721">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4722" />
                  <Input style={{
                  paddingLeft: "2.5rem"
                }} placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="" data-id="element-4723" />
                  
                </div>

                {canCreate('services') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4725" />} onClick={openNewForm} data-id="element-4724">
                  
                    Nuevo Servicio
                  </Button>}
              </div>

              {/* View Mode Tabs */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-4726">
                {[{
                key: 'active',
                label: 'Activos',
                count: activeCount,
                icon: CheckCircleIcon
              }, {
                key: 'archived',
                label: 'Archivados',
                count: archivedCount,
                icon: ArchiveIcon
              }, {
                key: 'deactivated',
                label: 'Desactivados',
                count: deactivatedCount,
                icon: XCircleIcon
              }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4727">
                  
                    <tab.icon className="w-4 h-4" data-id="element-4728" />
                    {tab.label}
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-4729">
                      {tab.count}
                    </span>
                  </button>)}
              </div>
            </div>

            {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-4730">
                <LockIcon className="w-4 h-4" data-id="element-4731" />
                <span data-id="element-4732">
                  Solo tienes permisos de lectura. No puedes crear, editar o
                  eliminar servicios.
                </span>
              </div>}

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4733">
              {paginatedServices.length === 0 ? <div className="col-span-full text-center py-16 text-gray-500 dark:text-gray-400" data-id="element-4734">
                  <div className="flex flex-col items-center justify-center" data-id="element-4735">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-4736">
                      <BriefcaseIcon className="w-8 h-8 text-gray-400" data-id="element-4737" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-4738">
                      No se encontraron servicios
                    </h3>
                    <p className="text-sm" data-id="element-4739">
                      {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo servicio' : 'No hay servicios en esta vista'}
                    </p>
                  </div>
                </div> : paginatedServices.map(service => <Card key={service.id} className="flex flex-col group overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-1 border-0 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/60 p-0" data-id="element-4740">
                {/* Card header with colored top band */}
                <div className="relative px-5 pt-4 pb-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800/40" data-id="element-4741">
                  {/* Top accent line */}
                  
                  <div className="flex items-start justify-between gap-2" data-id="element-4742">
                    <div className="flex-1 min-w-0" data-id="element-4743">
                      <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug truncate" data-id="element-4744">
                        {service.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-2" data-id="element-4745">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-blue-900/30 border text-xs font-medium" style={{
                        borderColor: "#1a9fd4",
                        color: "#1a9fd4"
                      }} data-id="element-4746">
                          <ClockIcon className="w-3 h-3" data-id="element-4747" />{service.duration} min
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right" data-id="element-4748">
                      <p className="text-xl font-extrabold leading-none" style={{
                      color: "#1a9fd4"
                    }} data-id="element-4749">
                        {formatCurrency(service.price)}
                      </p>
                      {service.sku && <p className="text-[10px] text-gray-400 font-mono mt-1" data-id="element-4750">{service.sku}</p>}
                    </div>
                  </div>
                  {/* Action buttons */}

                </div>
                {/* Card body */}
                <div className="flex flex-col flex-1 px-5 py-3 gap-2" data-id="element-4751">
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4752">
                    {service.description || <span className="italic text-gray-400 dark:text-gray-600" data-id="element-4753">Sin descripción</span>}
                  </p>
                  {/* Doctors */}
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60" data-id="element-4754">
                    <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5" data-id="element-4755">
                      <UserIcon className="w-3 h-3" data-id="element-4756" /> Doctores
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-4757">
                      {service.doctorIds && service.doctorIds.length > 0 ? service.doctorIds.map(docId => {
                      const doctor = getDoctor(docId);
                      return doctor ? <span key={docId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white dark:bg-blue-900/30 border text-xs font-medium" style={{
                        borderColor: "#1a9fd4",
                        color: "#1a9fd4"
                      }} data-id="element-4758">
                              <span className="w-4 h-4 rounded-full text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0" style={{
                          background: "#1a9fd4"
                        }} data-id="element-4759">
                                {doctor.name.charAt(0)}
                              </span>
                              {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}{doctor.name}
                            </span> : null;
                    }) : <span className="text-xs text-gray-400 italic" data-id="element-4760">Sin doctor asignado</span>}
                    </div>
                  </div>
                  {/* Action buttons at the very bottom border */}
                  <div className="flex justify-end gap-1 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1 opacity-0 group-hover:opacity-100 transition-all duration-150" data-id="element-4761">
                    {!readOnly && viewMode === 'active' ? <>
                      {canEdit('services') && <button onClick={() => openEditForm(service)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg transition-colors" title="Editar" data-id="element-4762"><EditIcon className="w-4 h-4" data-id="element-4763" /></button>}
                      {canDelete('services') && <>
                        <button onClick={() => openActionConfirm(service, 'archive')} className="p-1.5 text-gray-400 hover:text-amber-500 rounded-lg transition-colors" title="Archivar" data-id="element-4764"><ArchiveIcon className="w-4 h-4" data-id="element-4765" /></button>
                        <button onClick={() => openActionConfirm(service, 'deactivate')} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors" title="Desactivar" data-id="element-4766"><PowerOffIcon className="w-4 h-4" data-id="element-4767" /></button>
                      </>}
                    </> : !readOnly && viewMode !== 'active' ? <button onClick={() => restoreItem(service.id)} className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg transition-colors" title="Restaurar" data-id="element-4768"><RotateCcwIcon className="w-4 h-4" data-id="element-4769" /></button> : null}
                  </div>
                </div>
              </Card>)}
            </div>

            {/* Pagination */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4770">
              <Pagination currentPage={servicesPage} totalPages={servicesTotalPages} totalItems={servicesTotalItems} startIndex={servicesStartIndex} endIndex={servicesEndIndex} onPageChange={servicesGoToPage} onNext={servicesNextPage} onPrev={servicesPrevPage} data-id="element-4771" />
              
            </div>
          </div>
        </TabPanel>

        {/* ==================== TAB 2: ESTADÍSTICAS ==================== */}
        <TabPanel id="estadisticas" activeTab={activeTab} data-id="element-4772">
          <div className="space-y-6" data-id="element-4773">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4774">
              <Card className="bg-gradient-to-br from-blue-900 to-blue-600 text-white border-none" data-id="element-4775">
                <div className="flex items-center justify-between mb-2" data-id="element-4776">
                  <p className="text-blue-200 text-sm font-medium" data-id="element-4777">
                    Ingresos Totales
                  </p>
                  <DollarSignIcon className="w-5 h-5 text-blue-200" data-id="element-4778" />
                </div>
                <p className="text-2xl font-bold" data-id="element-4779">
                  {formatCurrency(kpiData.totalRevenue)}
                </p>
                <p className="text-xs text-blue-200 mt-1" data-id="element-4780">
                  En el periodo seleccionado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-emerald-500" data-id="element-4781">
                <div className="flex items-center justify-between mb-2" data-id="element-4782">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-4783">
                    Citas Completadas
                  </p>
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" data-id="element-4784" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4785">
                  {kpiData.totalCount}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4786">
                  Servicios realizados
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-blue-500" data-id="element-4787">
                <div className="flex items-center justify-between mb-2" data-id="element-4788">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-4789">
                    Ticket Promedio
                  </p>
                  <ActivityIcon className="w-5 h-5 text-blue-500" data-id="element-4790" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4791">
                  {formatCurrency(kpiData.avgTicket)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4792">
                  Por servicio realizado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-amber-500" data-id="element-4793">
                <div className="flex items-center justify-between mb-2" data-id="element-4794">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-4795">
                    Top Servicio
                  </p>
                  <TrendingUpIcon className="w-5 h-5 text-amber-500" data-id="element-4796" />
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate" title={kpiData.topService?.name || '-'} data-id="element-4797">
                  
                  {kpiData.topService?.name || '-'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4798">
                  {kpiData.topService ? formatCurrency(kpiData.topService.revenue) : '$0'}{' '}
                  generados
                </p>
              </Card>
            </div>

            {/* 1. FILTERS SECTION */}
            <Card data-id="element-4799">
              <CardHeader className="border-b border-gray-100 dark:border-gray-700 pb-4" data-id="element-4800">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-4801">
                  <FilterIcon className="w-4 h-4" data-id="element-4802" /> Configuración del Análisis
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6" data-id="element-4803">
                <div className="grid grid-cols-1 gap-4" data-id="element-4804">
                  {/* Date Range */}
                  <div className="space-y-2" data-id="element-4805">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4806">
                      Rango de Fecha
                    </label>
                    <div className="flex items-center gap-2" data-id="element-4807">
                      <Input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({
                      ...prev,
                      start: e.target.value
                    }))} max={dateRange.end || getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-4808" />
                      

                      <span className="text-gray-400" data-id="element-4809">-</span>
                      <Input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({
                      ...prev,
                      end: e.target.value
                    }))} max={getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-4810" />
                      
                    </div>
                  </div>

                  {/* Group By */}
                  <div className="space-y-2" data-id="element-4811">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4812">
                      Agrupar Por
                    </label>
                    <Select value={groupBy} onChange={setGroupBy} options={[{
                    value: 'day',
                    label: 'Día'
                  }, {
                    value: 'week',
                    label: 'Semana'
                  }, {
                    value: 'month',
                    label: 'Mes'
                  }]} data-id="element-4813" />
                    
                  </div>

                  {/* Service Selector */}
                  <div className="space-y-2" data-id="element-4814">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4815">
                      Comparar Servicios
                    </label>
                    <div className="relative" data-id="element-4816">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4817" />
                      <input type="text" placeholder="Buscar servicios..." value={compareSearchTerm} onChange={e => setCompareSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-4818" />
                      
                    </div>

                    {/* Selected Tags */}
                    {compareServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-4819">
                        {compareServiceIds.map((id, index) => {
                      const svc = services.find(s => s.id === id);
                      if (!svc) return null;
                      return <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-white shadow-sm" style={{
                        backgroundColor: COMPARE_COLORS[index % COMPARE_COLORS.length]
                      }} data-id="element-4820">
                            
                              {svc.name}
                              <button onClick={() => toggleCompareService(id)} className="hover:opacity-75" data-id="element-4821">
                              
                                <XIcon className="w-3 h-3" data-id="element-4822" />
                              </button>
                            </span>;
                    })}
                        <button onClick={() => setCompareServiceIds([])} className="text-xs text-gray-500 hover:text-red-500 px-1 font-medium" data-id="element-4823">
                        
                          Limpiar
                        </button>
                      </div>}

                    {/* Dropdown list */}
                    <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mt-2 p-1 bg-white dark:bg-gray-800 shadow-sm" data-id="element-4824">
                      {filteredCompareServices.map(service => <label key={service.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer transition-colors" data-id="element-4825">
                        
                          <input type="checkbox" checked={compareServiceIds.includes(service.id)} onChange={() => toggleCompareService(service.id)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-4826" />
                        

                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-4827">
                            {service.name}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. CHARTS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4828">
              {/* Quantity Chart */}
              <Card data-id="element-4829">
                <CardHeader data-id="element-4830">
                  <div className="flex items-center justify-between" data-id="element-4831">
                    <CardTitle className="flex items-center gap-2" data-id="element-4832">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-4833">
                        <BarChart3Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4834" />
                      </div>
                      Tendencia de Servicios
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Total Servicios',
                      key: 'total'
                    }];
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        total: d.totalCount
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Tendencia de Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-4835">
                      
                        <DownloadIcon className="w-4 h-4" data-id="element-4836" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-4837">
                  <div className="h-[220px] sm:h-[300px] w-full" data-id="element-4838">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4839">
                      <AreaChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-4840">
                        
                        <defs data-id="element-4841">
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1" data-id="element-4842">
                            
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-4843" />
                            

                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-4844" />
                            
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-4845" />
                        

                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-4846" />
                        

                        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} data-id="element-4847" />
                        

                        <Tooltip contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} data-id="element-4848" />
                        

                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-4849" />
                        

                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Area key={id} type="monotone" dataKey={svc.name} stroke={COMPARE_COLORS[index % COMPARE_COLORS.length]} fill={`url(#color${index})`} // Simplified for demo, ideally unique IDs
                        strokeWidth={2} fillOpacity={0.1} data-id="element-4850" />;
                      }) : <Area type="monotone" dataKey="totalCount" name="Total Servicios" stroke="#3b82f6" fill="url(#colorTotal)" strokeWidth={3} data-id="element-4851" />}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Chart */}
              <Card data-id="element-4852">
                <CardHeader data-id="element-4853">
                  <div className="flex items-center justify-between" data-id="element-4854">
                    <CardTitle className="flex items-center gap-2" data-id="element-4855">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-4856">
                        <DollarSignIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-4857" />
                      </div>
                      Ingresos Generados
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Ingresos Totales',
                      key: 'ingresos'
                    }];
                    const fmtCurrency = (n: number) => new Intl.NumberFormat('es-DO', {
                      style: 'currency',
                      currency: 'DOP',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(n);
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        ingresos: fmtCurrency(d.totalRevenue)
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Ingresos Generados por Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-4858">
                      
                        <DownloadIcon className="w-4 h-4" data-id="element-4859" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-4860">
                  <div className="h-[220px] sm:h-[300px] w-full" data-id="element-4861">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4862">
                      <BarChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-4863">
                        
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-4864" />
                        

                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-4865" />
                        

                        <YAxis stroke="#6b7280" fontSize={12} tickFormatter={v => `$${v}`} tickLine={false} axisLine={false} data-id="element-4866" />
                        

                        <Tooltip cursor={{
                        fill: 'rgba(0,0,0,0.05)'
                      }} contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} formatter={(value: number) => [formatCurrency(value), '']} data-id="element-4867" />
                        

                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-4868" />
                        

                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Bar key={id} dataKey={`${svc.name}_revenue`} name={svc.name} fill={COMPARE_COLORS[index % COMPARE_COLORS.length]} radius={[4, 4, 0, 0]} stackId="a" data-id="element-4869" />;
                      }) : <Bar dataKey="totalRevenue" name="Total Ingresos" fill="#10b981" radius={[6, 6, 0, 0]} data-id="element-4870" />}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 3. SUMMARY TABLE */}
            <Card className="overflow-hidden border-none shadow-lg" data-id="element-4871">
              <div className="h-1 bg-gradient-to-r from-blue-700 to-blue-500" data-id="element-4872" />
              <CardHeader data-id="element-4873">
                <div className="flex items-center justify-between" data-id="element-4874">
                  <CardTitle className="flex items-center gap-2" data-id="element-4875">
                    <ListIcon className="w-5 h-5 text-gray-600" data-id="element-4876" />
                    Detalle por Servicio
                  </CardTitle>
                  {canDownloadStatsPdf && summaryData.length > 0 && <button onClick={() => {
                  const detailData = summaryData.map(item => ({
                    name: item.name,
                    count: item.count,
                    totalRevenue: item.revenue,
                    avgPerAppointment: item.count > 0 ? item.revenue / item.count : 0,
                    percentOfTotal: kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0
                  }));
                  exportServiceDetailToPdf(detailData, kpiData.totalRevenue, kpiData.totalCount, {
                    from: dateRange.start,
                    to: dateRange.end
                  });
                }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-4877">
                    
                      <DownloadIcon className="w-4 h-4" data-id="element-4878" />
                    </button>}
                </div>
              </CardHeader>
              <CardContent className="p-0" data-id="element-4879">
                {/* ── MOBILE CARDS ── */}
                <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4880">
                  {summaryData.length === 0 ? <div className="py-10 text-center text-gray-500 text-sm" data-id="element-4881">
                      No hay datos para el periodo seleccionado
                    </div> : paginatedSummary.map((item, idx) => {
                  const percentage = kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0;
                  return <div key={idx} className="px-4 py-3" data-id="element-4882">
                          <div className="flex items-start justify-between gap-3 mb-2" data-id="element-4883">
                            <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-4884">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                          backgroundColor: '#3b82f6'
                        }} data-id="element-4885" />
                            

                              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4886">
                                {item.name}
                              </p>
                            </div>
                            <span className="text-sm font-bold text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4887">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 ml-4" data-id="element-4888">
                            <span data-id="element-4889">{item.count} citas</span>
                            <span data-id="element-4890">
                              Prom:{' '}
                              {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                            </span>
                            <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4891">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="mt-2 ml-4 w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4892">
                            <div className="h-full bg-blue-500 rounded-full transition-all" style={{
                        width: `${percentage}%`
                      }} data-id="element-4893" />
                          
                          </div>
                        </div>;
                })}
                  {summaryData.length > 0 && <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/80 border-t-2 border-gray-200 dark:border-gray-700" data-id="element-4894">
                      <div className="flex justify-between items-center" data-id="element-4895">
                        <span className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-4896">
                          TOTAL
                        </span>
                        <div className="text-right" data-id="element-4897">
                          <p className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-4898">
                            {formatCurrency(kpiData.totalRevenue)}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4899">
                            {kpiData.totalCount} citas
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>
                {/* ── DESKTOP TABLE ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-4900">
                  <table className="w-full" data-id="element-4901">
                    <thead data-id="element-4902">
                      <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" data-id="element-4903">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4904">
                          Servicio
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4905">
                          Cantidad
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4906">
                          Ingresos Totales
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-red-400 uppercase tracking-wider" data-id="element-4907">
                          Costo
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-green-500 uppercase tracking-wider" data-id="element-4908">
                          Ganancia
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4909">
                          Promedio / Cita
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4910">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4911">
                      {summaryData.length === 0 ? <tr data-id="element-4912">
                          <td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-4913">
                          
                            No hay datos para el periodo seleccionado
                          </td>
                        </tr> : paginatedSummary.map((item, idx) => {
                      const percentage = kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0;
                      return <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-4914">
                            
                              <td className="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4915">
                                <div className="flex items-center gap-3" data-id="element-4916">
                                  <div className="w-2.5 h-2.5 rounded-full" style={{
                              backgroundColor: compareServiceIds.length > 0 ? COMPARE_COLORS[compareServiceIds.indexOf(services.find(s => s.name === item.name)?.id || '') % COMPARE_COLORS.length] : '#3b82f6'
                            }} data-id="element-4917" />
                                

                                  {item.name}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-700 dark:text-gray-300 text-right" data-id="element-4918">
                                {item.count}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-900 dark:text-white font-semibold text-right" data-id="element-4919">
                                {formatCurrency(item.revenue)}
                              </td>
                              <td className="py-4 px-6 text-sm text-right" data-id="element-4920">
                                {(item as any).cost > 0 ? <span className="text-red-500 font-medium" data-id="element-4921">
                                    {formatCurrency((item as any).cost * item.count)}
                                  </span> : <span className="text-gray-300" data-id="element-4922">—</span>}
                              </td>
                              <td className="py-4 px-6 text-sm text-right" data-id="element-4923">
                                {(item as any).cost > 0 ? <span className="text-green-600 dark:text-green-400 font-semibold" data-id="element-4924">
                                    {formatCurrency(item.revenue - (item as any).cost * item.count)}
                                  </span> : <span className="text-gray-300" data-id="element-4925">—</span>}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 text-right" data-id="element-4926">
                                {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                              </td>
                              <td className="py-4 px-6 text-right" data-id="element-4927">
                                <div className="flex items-center justify-end gap-2" data-id="element-4928">
                                  <span className="text-xs text-gray-500" data-id="element-4929">
                                    {percentage.toFixed(1)}%
                                  </span>
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4930">
                                    <div className="h-full bg-indigo-500 rounded-full" style={{
                                width: `${percentage}%`
                              }} data-id="element-4931" />
                                  
                                  </div>
                                </div>
                              </td>
                            </tr>;
                    })}
                      {/* Total Row */}
                      {summaryData.length > 0 && <tr className="bg-gray-50 dark:bg-gray-800/80 font-bold border-t-2 border-gray-200 dark:border-gray-700" data-id="element-4932">
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white" data-id="element-4933">
                            TOTAL
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-4934">
                            {kpiData.totalCount}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-4935">
                            {formatCurrency(kpiData.totalRevenue)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-red-500" data-id="element-4936">
                            {formatCurrency(summaryData.reduce((sum, item) => sum + ((item as any).cost || 0) * item.count, 0))}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-green-600 dark:text-green-400" data-id="element-4937">
                            {formatCurrency(summaryData.reduce((sum, item) => sum + (item.revenue - ((item as any).cost || 0) * item.count), 0))}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-4938">
                            {formatCurrency(kpiData.avgTicket)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-4939">
                            100%
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination for Summary Table */}
            <Pagination currentPage={summaryPage} totalPages={summaryTotalPages} totalItems={summaryTotalItems} startIndex={summaryStartIndex} endIndex={summaryEndIndex} onPageChange={summaryGoToPage} onNext={summaryNextPage} onPrev={summaryPrevPage} data-id="element-4940" />
            
          </div>
        </TabPanel>
      </Tabs>

      {/* MODALS (Shared for Management) */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedService ? 'Editar Servicio' : 'Nuevo Servicio'} size="md" icon={<BriefcaseIcon className="w-5 h-5" data-id="element-4942" />} accentColor="blue" data-id="element-4941">
        
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4943">
          {/* Service Info */}
          <div data-id="element-4944">
            <div className="flex items-center gap-2 mb-4" data-id="element-4945">
              <span className="w-1.5 h-5 rounded-full bg-blue-600" data-id="element-4946"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4947">
                Información del Servicio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4948">
              <Input label="Nombre del Servicio" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Consulta General" data-id="element-4949" />
              

              <Input label="SKU (Código)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value.toUpperCase()
            })} placeholder="Ej: SRV-001" data-id="element-4950" />
              

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4951">
                <Input type="number" label="Duración (minutos)" value={formData.duration} onChange={e => setFormData({
                ...formData,
                duration: parseInt(e.target.value) || 0
              })} error={formErrors.duration} min={1} data-id="element-4952" />
                

                <Input type="number" label="Precio (DOP)" value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} error={formErrors.price} min={0} step="1" data-id="element-4953" />
                

                <Input type="number" label="Costo (DOP)" value={formData.cost} onChange={e => setFormData({
                ...formData,
                cost: e.target.value
              })} error={formErrors.cost} min={0} step="1" placeholder="0" data-id="element-4954" />
                
              </div>
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción del servicio..." data-id="element-4955" />
              
            </div>
          </div>

          {/* Doctor Assignment */}
          <div data-id="element-4956">
            <div className="flex items-center gap-2 mb-4" data-id="element-4957">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-4958"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4959">
                Doctores Asignados
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4960">
              <div className="max-h-48 overflow-y-auto space-y-1" data-id="element-4961">
                {activeDoctors.length === 0 ? <p className="text-sm text-gray-500 italic py-2" data-id="element-4962">
                    No hay doctores activos registrados
                  </p> : activeDoctors.map(doctor => {
                const isSelected = formData.doctorIds.includes(doctor.id);
                return <label key={doctor.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-800/30 shadow-sm' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-4963">
                      
                        <div className="relative" data-id="element-4964">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleDoctorSelection(doctor.id)} className="sr-only" data-id="element-4965" />
                        

                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600 dark:bg-blue-600 dark:border-blue-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-4966">
                          
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-4967">
                            
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-4968" />
                            
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-4969">
                        
                          {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}
                          {doctor.name}
                        </span>
                      </label>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-4970">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-4971">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4972">
              {selectedService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Servicio' : actionType === 'deactivate' ? 'Desactivar Servicio' : 'Eliminar Servicio'} size="sm" data-id="element-4973">
        
        <div className="space-y-4" data-id="element-4974">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-4975">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedService?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedService?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedService?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3 modal-actions" data-id="element-4976">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-4977">
              
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-4978">
              
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Servicios" isExporting={isExporting} data-id="element-4979" />
      

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Servicios a PDF" isExporting={isPdfExporting} data-id="element-4980" />
      
    </div>;
}